import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import GmailConnectPanel, { MailUnavailable } from './GmailConnectPanel';
import { useMailDraft } from './mailHooks';
import { C, S, btn } from './mailStyles';

const str = (v) => (typeof v === 'string' ? v : '');

function DraftShell({ children, testId }) {
  return (
    <div style={S.card} data-testid={testId}>
      <p style={S.title}>Draft reply</p>
      {children}
      <div style={S.actions}>
        <button type="button" style={btn(S.btnSecondary, true)} disabled>Rewrite with AI</button>
        <button type="button" style={btn(S.btnPrimary, true)} disabled>Send</button>
      </div>
    </div>
  );
}

function DraftEditor({ data, sendFollowUp }) {
  const [to, setTo] = useState(str(data?.to));
  const [subject, setSubject] = useState(str(data?.subject));
  const [body, setBody] = useState(str(data?.body));
  const [tapped, setTapped] = useState(null);

  const draftId = str(data?.draft_id);
  const edited = to !== str(data?.to) || subject !== str(data?.subject) || body !== str(data?.body);
  const locked = Boolean(tapped) || !draftId;
  const canSend = !locked && to.trim() !== '' && body.trim() !== '';

  const onRewrite = () => {
    if (locked) return;
    setTapped('rewrite');
    sendFollowUp('rewrite', 'Rewrite this draft', { mail_action: 'rewrite_draft', draft_id: draftId, body });
  };
  const onSend = () => {
    if (!canSend) return;
    setTapped('send');
    sendFollowUp('send', 'Send', { mail_action: 'send_draft', draft_id: draftId, edited, to, subject, body });
  };

  return (
    <div style={S.card} data-testid="mail-draft-card">
      <p style={S.title}>Draft reply</p>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={S.label}>To</span>
        <input style={S.input} value={to} disabled={locked} onChange={(e) => setTo(e.target.value)} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={S.label}>Subject</span>
        <input style={S.input} value={subject} disabled={locked} onChange={(e) => setSubject(e.target.value)} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={S.label}>Message</span>
        <textarea
          style={{ ...S.input, minHeight: 120, maxHeight: 280, overflowY: 'auto', resize: 'vertical', lineHeight: 1.45 }}
          value={body}
          disabled={locked}
          onChange={(e) => setBody(e.target.value)}
        />
      </label>
      {tapped && (
        <p style={S.sub}>{tapped === 'send' ? 'Preparing to send…' : 'Rewriting…'}</p>
      )}
      <div style={S.actions}>
        <button type="button" style={btn(S.btnSecondary, locked)} disabled={locked} onClick={onRewrite}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Sparkles size={14} color={C.accent} aria-hidden="true" />
            Rewrite with AI
          </span>
        </button>
        <button type="button" style={btn(S.btnPrimary, !canSend)} disabled={!canSend} onClick={onSend}>
          Send
        </button>
      </div>
    </div>
  );
}

export default function MailDraftCard({ data, sendFollowUp }) {
  const draftId = str(data?.draft_id);
  const hasBody = typeof data?.body === 'string';
  const live = useMailDraft(draftId, !hasBody);

  if (hasBody || live.state === 'idle') {
    return <DraftEditor key={draftId} data={data} sendFollowUp={sendFollowUp} />;
  }
  if (live.state === 'loading') {
    return (
      <DraftShell testId="mail-draft-loading">
        {['40%', '70%', '100%', '90%', '60%'].map((w, i) => (
          <div key={i} style={{ width: w, height: 12, borderRadius: 6, background: C.bgMuted }} />
        ))}
      </DraftShell>
    );
  }
  if (live.state === 'expired') {
    return (
      <DraftShell testId="mail-draft-expired">
        <p style={S.sub}>This draft is no longer available here — open Gmail Drafts</p>
      </DraftShell>
    );
  }
  if (live.state === 'auth') {
    return <GmailConnectPanel disconnected disconnectedSub="Reconnect to see this draft again." onConnected={live.reload} />;
  }
  if (live.state === 'disabled') return <MailUnavailable />;
  if (live.state === 'error') {
    return (
      <DraftShell testId="mail-draft-error">
        <p style={S.error}>Couldn't load this draft.</p>
        <button type="button" style={{ ...S.btnLink, alignSelf: 'flex-start' }} onClick={live.reload}>Retry</button>
      </DraftShell>
    );
  }
  const draft = live.draft || {};
  const merged = {
    ...data,
    to: str(draft.to) || str(data?.to),
    subject: str(draft.subject) || str(data?.subject),
    body: str(draft.body),
  };
  return <DraftEditor key={draftId} data={merged} sendFollowUp={sendFollowUp} />;
}
